/**
 * WarpGrep client for programmatic code search
 */

import { runWarpGrep, runWarpGrepStreaming } from './agent/runner.js';
import { logger } from '../../logger.js';
import { RemoteCommandsProvider } from './providers/remote.js';
import type { WarpGrepProvider } from './providers/types.js';
import type { AgentRunResult, WarpGrepStep } from './agent/types.js';
import type {
  WarpGrepClientConfig,
  WarpGrepInput,
  WarpGrepResult,
  WarpGrepContext,
  WarpGrepToolConfig,
  GitHubSearchInput,
  GitHubReadFileInput,
  GitHubReadFileResult,
  RemoteCommands,
} from './types.js';
import { createCodeStorageHttpCommands } from './providers/code_storage_http.js';
import { parseGitHubUrl } from './utils/github.js';
import { compactFileContexts } from '../compact/utils.js';
import { MorphAPIClient } from '../../core/client.js';
import { APIResource } from '../../core/resource.js';

const DEFAULT_CODE_SEARCH_URL = 'https://api.morphllm.com';

/**
 * Lazy-load LocalRipgrepProvider to avoid importing Node.js-specific
 * modules (child_process, fs) in edge environments like Cloudflare Workers.
 * Only loads when actually needed (i.e., when no remoteCommands or provider is specified).
 */
async function getLocalProvider(repoRoot: string, excludes?: string[], allowNames?: string[]): Promise<WarpGrepProvider> {
  const { LocalRipgrepProvider } = await import('./providers/local.js');
  const opts = allowNames?.length ? { allowNames } : undefined;
  return new LocalRipgrepProvider(repoRoot, excludes, opts);
}

/**
 * WarpGrep client for programmatic code search
 * 
 * @example
 * ```typescript
 * import { WarpGrepClient } from '@morphllm/morphsdk';
 * 
 * const client = new WarpGrepClient({ morphApiKey: process.env.MORPH_API_KEY });
 * 
 * // Simple usage - defaults to LocalRipgrepProvider
 * const result = await client.execute({
 *   searchTerm: 'Find authentication middleware',
 *   repoRoot: '.'
 * });
 *
 * // With custom excludes
 * const result = await client.execute({
 *   searchTerm: 'Find database models',
 *   repoRoot: '.',
 *   excludes: ['node_modules', '.git', 'dist']
 * });
 * ```
 *
 * @deprecated Prefer the unified `MorphClient` (`new MorphClient({ apiKey }).warpGrep`).
 * Standalone clients remain only for backwards compatibility and may be removed in a future
 * major version — do not use them in new code.
 */
export class WarpGrepClient extends APIResource {
  private config: WarpGrepClientConfig;

  constructor(clientOrConfig: MorphAPIClient | WarpGrepClientConfig = {}) {
    const isClient = clientOrConfig instanceof MorphAPIClient;
    super(
      isClient
        ? clientOrConfig
        : new MorphAPIClient({
            apiKey: clientOrConfig.morphApiKey,
            baseURL: clientOrConfig.morphApiUrl,
            timeout: clientOrConfig.timeout,
            retryConfig: clientOrConfig.retryConfig,
            debug: clientOrConfig.debug,
          }),
    );
    const cfg: WarpGrepClientConfig = isClient ? {} : clientOrConfig;
    // The agent loop talks to /v1/chat/completions through the `openai` package
    // (see agent/runner.ts), sourcing credentials/host/timeout from the transport.
    // `timeout` stays undefined-able so the runner's env-aware default still applies.
    this.config = {
      ...cfg,
      morphApiKey: this._client.resolveApiKey(),
      morphApiUrl: this._client.baseURL,
      timeout: this._client.timeout,
      retryConfig: this._client.retryConfig,
      debug: this._client.debug,
    };
  }

  /**
   * Execute a code search
   *
   * @param input - Search parameters including searchTerm, repoRoot, and optional provider
   * @returns Search results with relevant code contexts, or an AsyncGenerator if streamSteps is true
   *
   * @example
   * ```typescript
   * // Standard execution
   * const result = await client.execute({
   *   searchTerm: 'Find authentication middleware',
   *   repoRoot: '.'
   * });
   *
   * if (result.success) {
   *   for (const ctx of result.contexts) {
   *     console.log(`File: ${ctx.file}`);
   *     console.log(ctx.content);
   *   }
   * }
   *
   * // Streaming execution
   * const stream = client.execute({
   *   searchTerm: 'Find auth middleware',
   *   repoRoot: '.',
   *   streamSteps: true
   * });
   *
   * for await (const step of stream) {
   *   console.log(`Turn ${step.turn}:`, step.toolCalls);
   * }
   * ```
   */
  execute(input: WarpGrepInput & { streamSteps: true }): AsyncGenerator<WarpGrepStep, WarpGrepResult, undefined>;
  execute(input: WarpGrepInput & { streamSteps?: false | undefined }): Promise<WarpGrepResult>;
  execute(input: WarpGrepInput): Promise<WarpGrepResult> | AsyncGenerator<WarpGrepStep, WarpGrepResult, undefined> {
    const toolConfig: WarpGrepToolConfig = {
      repoRoot: input.repoRoot,
      remoteCommands: input.remoteCommands,
      provider: input.provider,
      excludes: input.excludes,
      includes: input.includes,
      debug: input.debug ?? this.config.debug,
      morphApiKey: this.config.morphApiKey,
      morphApiUrl: this.config.morphApiUrl,
      retryConfig: this.config.retryConfig,
      timeout: this.config.timeout,
      search_type: input.search_type,
    };

    if (input.streamSteps) {
      return executeToolCallStreaming({ search_term: input.searchTerm }, toolConfig);
    }
    return executeToolCall({ search_term: input.searchTerm }, toolConfig);
  }

  /**
   * Search a public GitHub repository
   *
   * @example
   * ```typescript
   * // Simple usage
   * const result = await client.searchGitHub({
   *   searchTerm: 'Find authentication middleware',
   *   github: 'vercel/next.js',
   * });
   *
   * // With streaming
   * const stream = client.searchGitHub({
   *   searchTerm: 'Find auth middleware',
   *   github: 'https://github.com/vercel/next.js',
   *   streamSteps: true,
   * });
   * for await (const step of stream) {
   *   console.log(`Turn ${step.turn}:`, step.toolCalls);
   * }
   * ```
   */
  searchGitHub(input: GitHubSearchInput & { streamSteps: true }): AsyncGenerator<WarpGrepStep, WarpGrepResult, undefined>;
  searchGitHub(input: GitHubSearchInput & { streamSteps?: false | undefined }): Promise<WarpGrepResult>;
  searchGitHub(input: GitHubSearchInput): Promise<WarpGrepResult> | AsyncGenerator<WarpGrepStep, WarpGrepResult, undefined> {
    if (input.streamSteps) {
      return this._searchGitHubStreaming(input);
    }
    return this._searchGitHubAsync(input);
  }

  /**
   * Read a single file from a public GitHub repository
   *
   * @example
   * ```typescript
   * const result = await client.readGitHubFile({
   *   github: 'vercel/next.js',
   *   path: 'src/server/app-render/index.tsx',
   *   startLine: 1,
   *   endLine: 50,
   * });
   * if (result.success) console.log(result.content);
   * ```
   */
  async readGitHubFile(input: GitHubReadFileInput): Promise<GitHubReadFileResult> {
    return executeGitHubReadFile(input, { timeout: this.config.timeout });
  }

  private async _resolveGitHubRepo(input: GitHubSearchInput): Promise<{ repo: string; remoteCommands: RemoteCommands }> {
    const { owner, repo } = parseGitHubUrl(input.github);
    const baseUrl = this.config.codeSearchUrl || DEFAULT_CODE_SEARCH_URL;
    logger.debug('WarpGrep', 'github_resolve', { owner_repo: `${owner}/${repo}`, branch: input.branch });

    const resolveStart = Date.now();
    const importRes = await fetch(`${baseUrl}/api/code-search/get-or-create?url=${encodeURIComponent(`${owner}/${repo}`)}`, {
      headers: this.config.morphApiKey ? { Authorization: `Bearer ${this.config.morphApiKey}` } : undefined,
    });
    if (!importRes.ok) {
      const text = await importRes.text().catch(() => importRes.statusText);
      logger.error('WarpGrep', 'github_resolve_failed', { owner_repo: `${owner}/${repo}`, status: importRes.status, error: text });
      throw new Error(`Failed to import repo ${owner}/${repo}: ${text}`);
    }
    const { repoId, defaultBranch } = await importRes.json();
    logger.debug('WarpGrep', 'github_repo_fetched', { repo_id: repoId, default_branch: defaultBranch, latency_ms: Date.now() - resolveStart });

    return {
      repo,
      remoteCommands: createCodeStorageHttpCommands({
        baseUrl,
        repoId,
        branch: input.branch || defaultBranch,
        apiKey: this.config.morphApiKey,
      }),
    };
  }

  private async _searchGitHubAsync(input: GitHubSearchInput): Promise<WarpGrepResult> {
    try {
      const { remoteCommands } = await this._resolveGitHubRepo(input);
      return this.execute({
        searchTerm: input.searchTerm,
        repoRoot: '.',
        remoteCommands,
      });
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : String(error) };
    }
  }

  private async *_searchGitHubStreaming(input: GitHubSearchInput): AsyncGenerator<WarpGrepStep, WarpGrepResult, undefined> {
    let remoteCommands: RemoteCommands;
    try {
      ({ remoteCommands } = await this._resolveGitHubRepo(input));
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : String(error) };
    }

    const generator = this.execute({
      searchTerm: input.searchTerm,
      repoRoot: '.',
      remoteCommands,
      streamSteps: true,
    });

    let result: WarpGrepResult;
    for (;;) {
      const { value, done } = await generator.next();
      if (done) {
        result = value;
        break;
      }
      yield value;
    }
    return result;
  }
}

/**
 * Execute a warp grep search directly
 *
 * @param input - Search parameters
 * @param config - Optional client configuration
 * @returns Search results, or an AsyncGenerator if streamSteps is true
 *
 * @example
 * ```typescript
 * import { executeWarpGrep } from '@morphllm/morphsdk/tools/warp-grep';
 *
 * const result = await executeWarpGrep({
 *   searchTerm: 'Find authentication middleware',
 *   repoRoot: '.'
 * });
 *
 * // Streaming
 * const stream = executeWarpGrep({
 *   searchTerm: 'Find auth',
 *   repoRoot: '.',
 *   streamSteps: true
 * });
 * for await (const step of stream) {
 *   console.log(step.turn, step.toolCalls);
 * }
 * ```
 */
export function executeWarpGrep(
  input: WarpGrepInput & { streamSteps: true },
  config?: WarpGrepClientConfig
): AsyncGenerator<WarpGrepStep, WarpGrepResult, undefined>;
export function executeWarpGrep(
  input: WarpGrepInput & { streamSteps?: false | undefined },
  config?: WarpGrepClientConfig
): Promise<WarpGrepResult>;
export function executeWarpGrep(
  input: WarpGrepInput,
  config?: WarpGrepClientConfig
): Promise<WarpGrepResult> | AsyncGenerator<WarpGrepStep, WarpGrepResult, undefined> {
  const client = new WarpGrepClient(config);
  if (input.streamSteps) {
    return client.execute({ ...input, streamSteps: true });
  }
  return client.execute({ ...input, streamSteps: false });
}

export async function executeToolCall(
  input: { search_term: string } | string,
  config: WarpGrepToolConfig
): Promise<WarpGrepResult> {
  const parsed = typeof input === 'string' ? JSON.parse(input) : input;

  // Priority: remoteCommands > provider > LocalRipgrepProvider (lazy-loaded for edge compatibility)
  const allowNames = config.search_type === 'node_modules' ? ['node_modules'] : undefined;
  const provider = config.remoteCommands
    ? new RemoteCommandsProvider(config.repoRoot, config.remoteCommands)
    : config.provider ?? await getLocalProvider(config.repoRoot, config.excludes, allowNames);

  const result = await runWarpGrep({
    searchTerm: parsed.search_term,
    repoRoot: config.repoRoot,
    provider,
    excludes: config.excludes,
    includes: config.includes,
    debug: config.debug ?? false,
    morphApiKey: config.morphApiKey,
    morphApiUrl: config.morphApiUrl,
    retryConfig: config.retryConfig,
    timeout: config.timeout,
    search_type: config.search_type,
  });

  return processAgentResult(result, parsed.search_term, config.morphApiKey, config.morphApiUrl);
}

/**
 * Helper to process AgentRunResult into WarpGrepResult
 */
async function processAgentResult(
  result: AgentRunResult,
  searchTerm: string,
  apiKey?: string,
  apiUrl?: string
): Promise<WarpGrepResult> {
  const finish = result.finish;
  if (result.terminationReason !== 'completed' || !finish?.metadata) {
    const errorDetails = result.errors?.map(e => e.message).join('; ') || 'unknown reason';
    console.error(`[warp_grep] processAgentResult failed. Reason: ${result.terminationReason}. Errors: ${errorDetails}. Turns: ${result.timings?.turns?.length ?? 0}`);
    return { success: false, error: `Search did not complete: ${errorDetails}` };
  }

  const contexts: WarpGrepContext[] = (finish.resolved ?? []).map(r => ({
    file: r.path,
    content: r.content,
    lines: r.ranges,
  }));

  try {
    const compactable = contexts.map(c => ({ path: c.file, content: c.content }));
    const compacted = await compactFileContexts(compactable, searchTerm, apiKey, apiUrl);
    for (let i = 0; i < contexts.length; i++) {
      contexts[i].content = compacted[i].content;
    }
  } catch (error) {
    console.error(`[warp_grep] Failed to compact result contexts:`, error);
  }

  return { success: true, contexts, summary: finish.payload };
}

/**
 * Streaming version of executeToolCall that yields WarpGrepStep for each turn.
 */
export async function* executeToolCallStreaming(
  input: { search_term: string } | string,
  config: WarpGrepToolConfig
): AsyncGenerator<WarpGrepStep, WarpGrepResult, undefined> {
  const parsed = typeof input === 'string' ? JSON.parse(input) : input;

  // Priority: remoteCommands > provider > LocalRipgrepProvider (lazy-loaded for edge compatibility)
  const allowNames = config.search_type === 'node_modules' ? ['node_modules'] : undefined;
  const provider = config.remoteCommands
    ? new RemoteCommandsProvider(config.repoRoot, config.remoteCommands)
    : config.provider ?? await getLocalProvider(config.repoRoot, config.excludes, allowNames);

  const generator = runWarpGrepStreaming({
    searchTerm: parsed.search_term,
    repoRoot: config.repoRoot,
    provider,
    excludes: config.excludes,
    includes: config.includes,
    debug: config.debug ?? false,
    morphApiKey: config.morphApiKey,
    morphApiUrl: config.morphApiUrl,
    retryConfig: config.retryConfig,
    timeout: config.timeout,
    search_type: config.search_type,
  });

  // Forward all yielded steps
  let agentResult: AgentRunResult;
  for (;;) {
    const { value, done } = await generator.next();
    if (done) {
      agentResult = value;
      break;
    }
    yield value;
  }

  // `await` is load-bearing: an async generator hands its `return` value back as
  // the `done` result without awaiting it, so returning the Promise directly would
  // surface an un-awaited Promise to streaming consumers instead of the result.
  return await processAgentResult(agentResult, parsed.search_term, config.morphApiKey, config.morphApiUrl);
}

/**
 * Fetch raw file content from a public GitHub repository.
 * Tries the given branch first, then falls back to main/master/develop.
 */
async function fetchRawGitHubFile(
  owner: string,
  repo: string,
  filePath: string,
  branch?: string,
  timeout?: number,
): Promise<{ content: string; branch: string }> {
  const branches = branch ? [branch] : ['HEAD'];
  const fallbacks = ['main', 'master', 'develop'];

  const fetchWithTimeout = (url: string) =>
    timeout ? fetch(url, { signal: AbortSignal.timeout(timeout) }) : fetch(url);

  for (const b of branches) {
    const url = `https://raw.githubusercontent.com/${owner}/${repo}/${b}/${filePath}`;
    const res = await fetchWithTimeout(url);
    if (res.ok) return { content: await res.text(), branch: b };
  }

  for (const fb of fallbacks) {
    if (branch && fb === branch) continue;
    const url = `https://raw.githubusercontent.com/${owner}/${repo}/${fb}/${filePath}`;
    const res = await fetchWithTimeout(url);
    if (res.ok) return { content: await res.text(), branch: fb };
  }

  throw new Error(`File not found: ${owner}/${repo}/${filePath}`);
}

/**
 * Read a single file (or line range) from a public GitHub repository
 */
export async function executeGitHubReadFile(
  input: GitHubReadFileInput,
  config?: { timeout?: number },
): Promise<GitHubReadFileResult> {
  try {
    const { owner, repo } = parseGitHubUrl(input.github);
    const { content: raw, branch } = await fetchRawGitHubFile(
      owner, repo, input.path, input.branch, config?.timeout,
    );

    const allLines = raw.split('\n');
    const totalLines = allLines.length;
    const start = Math.max(1, input.startLine ?? 1);
    const end = Math.min(totalLines, input.endLine ?? totalLines);

    const selected = allLines.slice(start - 1, end);
    const padWidth = String(end).length;
    const numbered = selected
      .map((line, i) => `${String(start + i).padStart(padWidth)}|${line}`)
      .join('\n');

    return {
      success: true,
      content: numbered,
      path: input.path,
      github: `${owner}/${repo}`,
      branch,
      lineRange: [start, end],
      totalLines,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * Format a GitHub read file result for display or tool responses
 */
export function formatGitHubReadFileResult(result: GitHubReadFileResult): string {
  if (!result.success) {
    return `Failed to read file: ${result.error}`;
  }

  const rangeStr = result.lineRange
    ? ` (lines ${result.lineRange[0]}-${result.lineRange[1]} of ${result.totalLines})`
    : '';

  return `File: ${result.github}/${result.path}${rangeStr}\n\n${result.content}`;
}

/**
 * Format warp grep results for display or tool responses
 * 
 * @param result - The search result
 * @returns Formatted string representation
 */
export function formatResult(result: WarpGrepResult): string {
  if (!result.success) {
    return `Search failed: ${result.error}`;
  }

  if (!result.contexts || result.contexts.length === 0) {
    return 'No relevant code found. Try rephrasing your search term.';
  }

  const parts: string[] = [];
  parts.push(`Morph Fast Context subagent performed search on repository:\n`);
  parts.push('Relevant context found:');

  for (const ctx of result.contexts) {
    const rangeStr = !ctx.lines || ctx.lines === '*'
      ? '*'
      : ctx.lines.map(([s, e]) => `${s}-${e}`).join(',');
    parts.push(`- ${ctx.file}:${rangeStr}`);
  }

  parts.push('\nHere is the content of files:\n');

  for (const ctx of result.contexts) {
    const rangeStr = !ctx.lines || ctx.lines === '*'
      ? ''
      : ` lines="${ctx.lines.map(([s, e]) => `${s}-${e}`).join(',')}"`;
    parts.push(`<file path="${ctx.file}"${rangeStr}>`);
    parts.push(ctx.content);
    parts.push('</file>\n');
  }

  return parts.join('\n');
}

